import { EDecision, EFinancingProgram } from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import { Gavel as GavelIcon, History as HistoryIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material'
import { Button, Divider, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { CreditDecisionOverrideLimits } from '@src/data/types/CreditDecisionOverrideLimits'
import { getApiClient } from '@src/services/api-client'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import allApis from '../../../data/api'
import { useApplySwapApplicants } from '../../../data/api/swap-applicants-roles/swap-applicants-roles-api'
import { TRootState, useAppSelector } from '../../../data/store'
import { appSelectors } from '../../../data/store/AppStore'
import { creditEffects, creditSelectors } from '../../../data/store/CreditApplication'
import { fullCreditApplicationEffects } from '../../../data/store/FullCreditApplication'
import { Constants, CreditApplication } from '../../../data/types'
import { FinalCreditDecision } from '../../../data/types/CreditDecisionSchema'
import { TranslatedEnum } from '../../../data/types/TranslatedEnum'
import FormattedMerchantPaymentPlan from '../../ViewCreditApplicationPage/components/FormattedMerchantPaymentPlan'
import CreditDecisionDialog from './creditDecisionDialog'
import DecisonIcon from './decisonIcon'
import SwapApplicantsSimulationDialog from './SwapApplicantsSimulationDialog'

interface Props {
  creditApplication: CreditApplication
  canApprove: boolean
  userCanApproveCreditApplication: boolean
  financingProgramId: EFinancingProgram
  editDisabled: boolean
  openHistoryDialog: () => void
}

const CreditDecisionIceberg = ({
  creditApplication,
  canApprove,
  userCanApproveCreditApplication,
  financingProgramId,
  editDisabled,
  openHistoryDialog,
}: Props) => {
  const apiClient = getApiClient()
  const applySwapApplicants = useApplySwapApplicants()

  const [openIceberDecisionDialog, setOpenIceberDecisionDialog] = useState<boolean>(false)
  const [creditDecisionOverrideLimits, setCreditDecisionOverrideLimits] = useState<CreditDecisionOverrideLimits | null>(
    null,
  )
  const [openSwapDialog, setOpenSwapDialog] = useState(false)
  const { t } = useTranslation()

  const planEnum = useAppSelector((state: TRootState) =>
    appSelectors.getPlanEnumForProgram(state, creditApplication.financingProgramId),
  )
  const lang = useAppSelector(appSelectors.getCurrentLang) as keyof TranslatedEnum
  const merchantPaymentPlan = useAppSelector(creditSelectors.getMerchantPaymentPlan)
  const maxInterestRate = Constants.financingProgramConfigs[creditApplication.financingProgramId].maxInterestRate

  const dispatchEffect = useSideEffect()
  const theme = useTheme()

  const hasNormCreditDecision = creditApplication.normsCreditDecision !== null
  const hasCoapplicant = !!creditApplication.coApplicant

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (
          creditApplication.id &&
          creditApplication.financingProgramId &&
          apiClient &&
          creditApplication.normsCreditDecision?.decision !== EDecision.Refused
        ) {
          const fetchedCreditDecisionOverrideLimits = await allApis.credit.getCreditDecisionOverrideLimits(apiClient, {
            financingProgramId: creditApplication.financingProgramId,
            creditApplicationId: creditApplication.id,
          })
          setCreditDecisionOverrideLimits(fetchedCreditDecisionOverrideLimits)
        }
      } catch (fetchingError) {
        console.error('Error fetching credit decision overrides:', fetchingError)
      }
    }

    fetchData().catch(reportErrorToConsole)
  }, [creditApplication, apiClient])

  const handleSaveDecison = useCallback(
    async (value: FinalCreditDecision) => {
      setOpenIceberDecisionDialog(false)
      value.versionTag = creditApplication.versionTag
      value.financingProgramId = creditApplication.financingProgramId
      await dispatchEffect(creditEffects.saveCreditDecision(value))
      return await dispatchEffect(
        fullCreditApplicationEffects.getById(creditApplication.id, creditApplication.financingProgramId),
      )
    },
    [creditApplication, dispatchEffect],
  )
  const shoulDisplayCreditDecisionfields = useAppSelector(
    creditSelectors.getFinancingProgramConfigs,
  ).useCreditDecisionFields

  return (
    <div>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        alignSelf="center"
      >
        <Typography variant="h6" component="h3" gutterBottom>
          {t('credit.finalDecision')}
        </Typography>
        <IconButton onClick={openHistoryDialog}>
          <HistoryIcon fontSize="large" color="primary" />
        </IconButton>
      </Stack>

      <DecisonIcon decision={creditApplication.finalCreditDecision.decision} />
      {merchantPaymentPlan ? (
        <Stack justifyContent="center" direction="row" sx={{ backgroundColor: theme.palette.action.selected }}>
          <Typography style={{ marginRight: '1rem' }}>Promo:</Typography>
          <FormattedMerchantPaymentPlan merchantPaymentPlan={merchantPaymentPlan} />
        </Stack>
      ) : (
        <Typography
          variant="subtitle1"
          sx={{ backgroundColor: theme.palette.action.selected, mt: theme.spacing(1) }}
          component="p"
        >
          {t('credit.interestRate')} :{' '}
          {creditApplication?.normsCreditDecision == null
            ? maxInterestRate
            : creditApplication.finalCreditDecision.interestRate}{' '}
          %
        </Typography>
      )}
      {shoulDisplayCreditDecisionfields && (
        <>
          <Typography
            variant="subtitle1"
            sx={{ backgroundColor: theme.palette.action.selected, mt: theme.spacing(1) }}
            component="p"
          >
            {t('credit.plan')}:{' '}
            {creditApplication.normsCreditDecision?.planName != null
              ? planEnum[creditApplication.normsCreditDecision.planName ?? ''][lang]
              : 'N/A'}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ backgroundColor: theme.palette.action.selected, mt: theme.spacing(1) }}
            component="p"
          >
            {t('credit.authorizedLoanAmount')}:{' '}
            {formatCurrencyNoDecimals(creditApplication?.finalCreditDecision?.maxAmountFinanced)}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ backgroundColor: theme.palette.action.selected, mt: theme.spacing(1) }}
            component="p"
          >
            {t('credit.maxPayment')}: {formatCurrencyNoDecimals(creditApplication.finalCreditDecision.maxPmtAmount)}
          </Typography>
        </>
      )}
      <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
        <Button
          id="btnEditCreditDecision"
          variant="contained"
          startIcon={<GavelIcon />}
          size="large"
          onClick={() => setOpenIceberDecisionDialog(true)}
          disabled={
            editDisabled ||
            !userCanApproveCreditApplication ||
            creditApplication.normsCreditDecision?.decision === EDecision.Refused ||
            !hasNormCreditDecision
          }
        >
          {t('credit.decision')}
        </Button>
      </Stack>

      {hasCoapplicant && (
        <>
          <Stack direction="row" alignItems="center" sx={{ my: 3 }}>
            <Divider flexItem sx={{ flexGrow: 1 }} />
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <Button
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              size="large"
              onClick={() => {
                setOpenSwapDialog(true)
              }}
            >
              {t('credit.switchOffer')}
            </Button>
          </Stack>
          <SwapApplicantsSimulationDialog
            open={openSwapDialog}
            onClose={() => {
              if (!applySwapApplicants.isPending) setOpenSwapDialog(false)
            }}
            onApply={async () => {
              try {
                await applySwapApplicants.mutateAsync({
                  financingProgramId: creditApplication.financingProgramId,
                  creditApplicationId: creditApplication.id,
                })
                await dispatchEffect(
                  fullCreditApplicationEffects.getById(creditApplication.id, creditApplication.financingProgramId),
                )
                setOpenSwapDialog(false)
              } catch (error) {
                console.error('Error applying simulation:', error)
              }
            }}
            disabled={editDisabled}
            isApplying={applySwapApplicants.isPending}
            creditApplication={creditApplication}
          />
        </>
      )}
      {hasNormCreditDecision && (
        <CreditDecisionDialog
          open={openIceberDecisionDialog}
          creditApplicationDecisionOverrideLimits={creditDecisionOverrideLimits}
          canApprove={canApprove}
          financingProgramId={financingProgramId}
          onCancel={() => setOpenIceberDecisionDialog(false)}
          onConfirm={handleSaveDecison}
          creditApplication={creditApplication}
        />
      )}
    </div>
  )
}

export default CreditDecisionIceberg
